import {type AtIdentifierString, AtUri} from '@atproto/syntax'
import {parse} from 'psl'
import TLDs from 'tlds'

import {BSKY_SERVICE, SUNNAHSKY_APP_URL} from '#/lib/constants'
import {
  isCatcherHandle,
  isInvalidHandle,
  isSunnahskyHandle,
} from '#/lib/strings/handles'
import {startUriToStarterPackUri} from '#/lib/strings/starter-pack'
import {logger} from '#/logger'

export const BSKY_APP_HOST = 'https://bsky.app'
export const BSKY_HOSTING_ENDSWITH = '.host.bsky.network'
const BSKY_TRUSTED_HOSTS = [
  'bsky\\.app',
  'bsky\\.social',
  'blueskyweb\\.xyz',
  'blueskyweb\\.zendesk\\.com',
  // "PDS hostname move and public URL scheme" - sunnahsky.com is the
  // app's own origin, so its links must stop tripping the leaving-the-app
  // phishing-mismatch warning the same way bsky.app's do.
  'sunnahsky\\.com',
  ...(__DEV__ ? ['localhost:19006', 'localhost:8100'] : []),
]

/*
 * This will allow any BSKY_TRUSTED_HOSTS value by itself or with a subdomain.
 * It will also allow relative paths like /profile as well as #.
 *
 * The `([/?#]|$)` after the host group is a security-review fix (finding
 * 4): without an end boundary there, the host alternation is only ever
 * checked as an unanchored prefix, so `https://bsky.app.evil.com/`,
 * `https://sunnahsky.com.evil.net/login`, and `https://sunnahsky.community/`
 * all satisfied the old regex and suppressed the phishing mismatch warning
 * for a URL that only *starts with* a trusted host's characters. This was
 * already a real bug before "PDS hostname move and public URL scheme"; it
 * matters now because this is the first host list entry the project itself
 * owns, not just relies on upstream.
 */
const TRUSTED_REGEX = new RegExp(
  `^(http(s)?://(([\\w-]+\\.)?${BSKY_TRUSTED_HOSTS.join(
    '|([\\w-]+\\.)?',
  )})([/?#]|$)|/|#)`,
)

export function canParseUrl(url: string | URL, base?: string | URL): boolean {
  try {
    new URL(url, base)
    return true
  } catch {
    return false
  }
}

export function isValidDomain(str: string): boolean {
  return !!TLDs.find(tld => {
    let i = str.lastIndexOf(tld)
    if (i === -1) {
      return false
    }
    return str.charAt(i - 1) === '.' && i === str.length - tld.length
  })
}

export function makeRecordUri(
  didOrName: string,
  collection: string,
  rkey: string,
) {
  const urip = new AtUri('at://placeholder.placeholder/')
  // the helper takes the did or handle as a plain string
  urip.host = didOrName as AtIdentifierString
  urip.collection = collection
  urip.rkey = rkey
  return urip.toString()
}

export function toNiceDomain(url: string): string {
  try {
    const urlp = new URL(url)
    if (`https://${urlp.host}` === BSKY_SERVICE) {
      return 'Bluesky Social'
    }
    return urlp.host ? urlp.host : url
  } catch (e) {
    return url
  }
}

export function toShortUrl(url: string): string {
  try {
    const urlp = new URL(url)
    if (urlp.protocol !== 'http:' && urlp.protocol !== 'https:') {
      return url
    }
    const path =
      (urlp.pathname === '/' ? '' : urlp.pathname) + urlp.search + urlp.hash
    if (path.length > 15) {
      return urlp.host + path.slice(0, 13) + '...'
    }
    return urlp.host + path
  } catch (e) {
    return url
  }
}

export function toShareUrl(url: string): string {
  if (!url.startsWith('https')) {
    const urlp = new URL(SUNNAHSKY_APP_URL)
    urlp.pathname = url
    url = urlp.toString()
  }
  return url
}

export function toBskyAppUrl(url: string): string {
  return new URL(url, BSKY_APP_HOST).toString()
}

export function toNiceHostingUrl(url: string): string {
  try {
    const urlp = new URL(url)
    if (urlp.host.endsWith(BSKY_HOSTING_ENDSWITH)) {
      return 'Bluesky'
    }
    return urlp.host
  } catch {
    return url
  }
}

/**
 * Whether the given service URL points at a Bluesky-operated PDS. True when the
 * host is `bsky.social` (the {@link BSKY_SERVICE} host) or ends with
 * `.host.bsky.network`. Returns false if the URL can't be parsed.
 */
export function isBlueskyHostedUrl(url: string): boolean {
  try {
    const {host} = new URL(url)
    return (
      host === new URL(BSKY_SERVICE).host ||
      host.endsWith(BSKY_HOSTING_ENDSWITH)
    )
  } catch {
    return false
  }
}

export function isBskyAppUrl(url: string): boolean {
  return url.startsWith('https://bsky.app/')
}

export function isSunnahskyAppUrl(url: string): boolean {
  return url.startsWith(`${SUNNAHSKY_APP_URL}/`)
}

export function isRelativeUrl(url: string): boolean {
  return /^\/[^/]/.test(url)
}

export function isBskyRSSUrl(url: string): boolean {
  return (
    (url.startsWith('https://bsky.app/') || isRelativeUrl(url)) &&
    /\/rss\/?$/.test(url)
  )
}

export function isExternalUrl(url: string): boolean {
  const external = !isBskyAppUrl(url) && url.startsWith('http')
  const rss = isBskyRSSUrl(url)
  return external || rss
}

export function isTrustedUrl(url: string): boolean {
  return TRUSTED_REGEX.test(url)
}

export function isBskyPostUrl(url: string): boolean {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return /profile\/(?<name>[^/]+)\/post\/(?<rkey>[^/]+)/i.test(
        urlp.pathname,
      )
    } catch {}
  }
  return false
}

/**
 * Matches both article URL shapes this app emits: the legacy
 * `/article/:did/:rkey` alias `articleUrl()` (`lib/api/articles.ts`) used
 * to build unconditionally, and the canonical `/:name/article/:slug` form
 * "PDS hostname move and public URL scheme" introduces. Confirmed directly
 * against both builders rather than assumed.
 */
export function isSunnahskyArticleUrl(url: string): boolean {
  if (isSunnahskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return (
        /^\/article\/(?<did>[^/]+)\/(?<rkey>[^/]+)/i.test(urlp.pathname) ||
        /^\/(?<name>[^/]+)\/article\/(?<slug>[^/]+)/i.test(urlp.pathname)
      )
    } catch {}
  }
  return false
}

export function isBskyCustomFeedUrl(url: string): boolean {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return /profile\/(?<name>[^/]+)\/feed\/(?<rkey>[^/]+)/i.test(
        urlp.pathname,
      )
    } catch {}
  }
  return false
}

export function isBskyListUrl(url: string): boolean {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return /profile\/(?<name>[^/]+)\/lists\/(?<rkey>[^/]+)/i.test(
        urlp.pathname,
      )
    } catch {
      console.error('Unexpected error in isBskyListUrl()', url)
    }
  }
  return false
}

export function isBskyStartUrl(url: string): boolean {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return /start\/(?<name>[^/]+)\/(?<rkey>[^/]+)/i.test(urlp.pathname)
    } catch {
      console.error('Unexpected error in isBskyStartUrl()', url)
    }
  }
  return false
}

export function isBskyStarterPackUrl(url: string): boolean {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      return /starter-pack\/(?<name>[^/]+)\/(?<rkey>[^/]+)/i.test(urlp.pathname)
    } catch {
      console.error('Unexpected error in isBskyStartUrl()', url)
    }
  }
  return false
}

// Invite codes are 7 alphanumeric characters long, supporting up to 10 here to future-proof.
export const CHAT_INVITE_CODE_REGEX = /^\/chat\/([a-zA-Z0-9]{7,10})$/

export function getChatInviteCodeFromUrl(url: string): string | undefined {
  let pathname: string
  if (isBskyAppUrl(url)) {
    try {
      pathname = new URL(url).pathname
    } catch {
      return undefined
    }
  } else if (url.startsWith('/')) {
    pathname = url.split('?')[0].split('#')[0]
  } else {
    return undefined
  }
  return pathname.match(CHAT_INVITE_CODE_REGEX)?.[1]
}

export function isBskyChatInviteUrl(url: string): boolean {
  return getChatInviteCodeFromUrl(url) !== undefined
}

export function isBskyDownloadUrl(url: string): boolean {
  if (isExternalUrl(url)) {
    return false
  }
  return url === '/download' || url.startsWith('/download?')
}

/**
 * `convertBskyAppUrlIfNeeded`'s own bsky.app branch converts ANY bsky.app
 * profile/post URL to an internal relative path unconditionally, which is
 * exactly right for its actual callers (resolve.ts's post/feed/list embed
 * resolution, live-events, message-embed previews - all "fetch and show
 * whatever this points at", membership-irrelevant) but wrong for Link.tsx's
 * navigation decision: converting a bsky.app link to a *real Bluesky-only*
 * account into a relative path makes it navigate in-app, which is exactly
 * the external-content leak "close off external content plan.md" spent an
 * entire project scoping closed everywhere else. This is that plan's one
 * remaining open surface (Phase H) - "PDS hostname move and public URL
 * scheme" closes it here rather than widening the shared helper (and
 * risking the other four callers) with a membership check they don't want.
 *
 * A handle-form subject is decided synchronously and needs no DID set at
 * all: the PDS is the only issuer of Sunnahsky/Catcher handles, so
 * `isSunnahskyHandle`/`isCatcherHandle` passing is membership by
 * construction, and an unregistered handle converts to a path that
 * `ProfileScreen` already renders as Not Found rather than a foreign
 * profile. A DID-form subject needs the `sunnahskyDids` set from
 * `useSunnahskyDids()` - passed in explicitly (via {@link useConvertUrl}
 * below) rather than made async here, matching this project's existing
 * derived-data pattern. **Fails closed while that set is still loading**
 * (security review, finding 7): `sunnahskyDids === undefined` is
 * indistinguishable from "hasn't resolved yet", so a DID-form link is
 * treated as external in that state rather than assumed a member - the
 * omission that has been caught three times before in this codebase.
 * Handle-form links need no such gate, since they depend on no async data.
 *
 * Only the `/profile/*` family is classified below - every other bsky.app
 * path (starter packs, `/start/*`, or anything else) has no membership
 * check defined for it, so it is returned unconverted rather than falling
 * through to an unconditional relative-path rewrite. That fallthrough was
 * a real bug (security review, finding 1): it let an outside account's
 * starter-pack link (or any other unclassified bsky.app path) convert to
 * a relative path that this app's own router would then match and render
 * in-app, the exact external-content leak this function exists to close.
 */
export function convertBskyAppUrlForLink(
  url: string,
  sunnahskyDids: Set<string> | undefined,
): string {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)
      const match = /^\/profile\/(?<subject>[^/]+)(\/.*)?$/i.exec(urlp.pathname)
      if (match?.groups?.subject) {
        const subject = match.groups.subject
        const isMember = subject.startsWith('did:')
          ? sunnahskyDids !== undefined && sunnahskyDids.has(subject)
          : isSunnahskyHandle(subject) || isCatcherHandle(subject)
        return isMember ? urlp.pathname + urlp.search : url
      }
      return url
    } catch (e) {
      console.error('Unexpected error in convertBskyAppUrlForLink()', e)
      return url
    }
  }
  // Not a bsky.app URL - sunnahsky.com's own canonical URLs (e.g. the
  // article-reader's `https://sunnahsky.com/article/:did/:rkey` links)
  // still need converting to a relative path here, since they carry no
  // membership ambiguity (this app's own domain, not an outside host).
  return convertBskyAppUrlIfNeeded(url)
}

export function convertBskyAppUrlIfNeeded(url: string): string {
  if (isBskyAppUrl(url)) {
    try {
      const urlp = new URL(url)

      if (isBskyStartUrl(url)) {
        return startUriToStarterPackUri(urlp.pathname)
      }

      return urlp.pathname + urlp.search
    } catch (e) {
      console.error('Unexpected error in convertBskyAppUrlIfNeeded()', e)
    }
  } else if (isSunnahskyAppUrl(url)) {
    /*
     * The linchpin for the article reader: `StandardSiteEmbed` (the card
     * every companion post already renders) navigates via `to={view.uri}` -
     * the article's own `https://sunnahsky.com/article/:did/:rkey` URL -
     * through this app's own `Link` component. Once that URL converts to a
     * relative path here, `isExternalUrl()` (which only ever checks
     * `startsWith('http')`) naturally treats it as internal too, with no
     * separate change needed there - exactly the same mechanism
     * `isBskyAppUrl` already relies on above. Every existing article card
     * starts opening the in-app reader with zero changes to
     * `StandardSiteEmbed` itself.
     */
    try {
      const urlp = new URL(url)
      return urlp.pathname + urlp.search
    } catch (e) {
      console.error('Unexpected error in convertBskyAppUrlIfNeeded()', e)
    }
  } else if (isShortLink(url)) {
    // We only want to do this on native, web handles the 301 for us
    return shortLinkToHref(url)
  }
  return url
}

export function listUriToHref(url: string): string {
  try {
    const {hostname, rkey} = new AtUri(url)
    return `/profile/${hostname}/lists/${rkey}`
  } catch {
    return ''
  }
}

export function feedUriToHref(url: string): string {
  try {
    const {hostname, rkey} = new AtUri(url)
    return `/profile/${hostname}/feed/${rkey}`
  } catch {
    return ''
  }
}

export function postUriToRelativePath(
  uri: string,
  options?: {handle?: string},
): string | undefined {
  try {
    const {hostname, rkey} = new AtUri(uri)
    const handleOrDid =
      options?.handle && !isInvalidHandle(options.handle)
        ? options.handle
        : hostname
    return `/profile/${handleOrDid}/post/${rkey}`
  } catch {
    return undefined
  }
}

/**
 * Checks if the label in the post text matches the host of the link facet.
 *
 * Hosts are case-insensitive, so should be lowercase for comparison.
 * @see https://www.rfc-editor.org/rfc/rfc3986#section-3.2.2
 */
export function linkRequiresWarning(uri: string, label: string) {
  const labelDomain = labelToDomain(label)

  // We should trust any relative URL or a # since we know it links to internal content
  if (isRelativeUrl(uri) || uri === '#') {
    return false
  }

  let urip
  try {
    urip = new URL(uri)
  } catch {
    return true
  }

  const host = urip.hostname.toLowerCase()
  if (isTrustedUrl(uri)) {
    // if this is a link to internal content, warn if it represents itself as a URL to another app
    return !!labelDomain && labelDomain !== host && isPossiblyAUrl(labelDomain)
  } else {
    // if this is a link to external content, warn if the label doesnt match the target
    if (!labelDomain) {
      return true
    }
    return labelDomain !== host
  }
}

/**
 * Returns a lowercase domain hostname if the label is a valid URL.
 *
 * Hosts are case-insensitive, so should be lowercase for comparison.
 * @see https://www.rfc-editor.org/rfc/rfc3986#section-3.2.2
 */
export function labelToDomain(label: string): string | undefined {
  // any spaces just immediately consider the label a non-url
  if (/\s/.test(label)) {
    return undefined
  }
  try {
    return new URL(label).hostname.toLowerCase()
  } catch {}
  try {
    return new URL('https://' + label).hostname.toLowerCase()
  } catch {}
  return undefined
}

export function isPossiblyAUrl(str: string): boolean {
  str = str.trim()
  if (str.startsWith('http://')) {
    return true
  }
  if (str.startsWith('https://')) {
    return true
  }
  const [firstWord] = str.split(/[\s\/]/)
  return isValidDomain(firstWord)
}

export function splitApexDomain(hostname: string): [string, string] {
  const hostnamep = parse(hostname)
  if (hostnamep.error || !hostnamep.listed || !hostnamep.domain) {
    return ['', hostname]
  }
  return [
    hostnamep.subdomain ? `${hostnamep.subdomain}.` : '',
    hostnamep.domain,
  ]
}

export function createAppAbsoluteUrl(path: string): string {
  const sanitizedPath = path.replace(SUNNAHSKY_APP_URL, '').replace(/^\/+/, '')
  return `${SUNNAHSKY_APP_URL.replace(/\/$/, '')}/${sanitizedPath}`
}

export function createProxiedUrl(url: string): string {
  let u
  try {
    u = new URL(url)
  } catch {
    return url
  }

  if (u?.protocol !== 'http:' && u?.protocol !== 'https:') {
    return url
  }

  return `https://go.bsky.app/redirect?u=${encodeURIComponent(url)}`
}

export function isShortLink(url: string): boolean {
  return url.startsWith('https://go.bsky.app/')
}

export function shortLinkToHref(url: string): string {
  try {
    const urlp = new URL(url)

    // For now we only support Starter Packs, but in the future we should add additional paths to this check
    const parts = urlp.pathname.split('/').filter(Boolean)
    if (parts.length === 1) {
      return `/starter-pack-short/${parts[0]}`
    }
    return url
  } catch (e) {
    logger.error('Failed to parse possible short link', {safeMessage: e})
    return url
  }
}

export function getHostnameFromUrl(url: string | URL): string | null {
  let urlp
  try {
    urlp = new URL(url)
  } catch (e) {
    return null
  }
  return urlp.hostname
}

export function getServiceAuthAudFromUrl(url: string | URL): string | null {
  const hostname = getHostnameFromUrl(url)
  if (!hostname) {
    return null
  }
  return `did:web:${hostname}`
}

// passes URL.parse, and has a TLD etc
export function definitelyUrl(maybeUrl: string) {
  try {
    if (maybeUrl.endsWith('.')) return null

    // Prepend 'https://' if the input doesn't start with a protocol
    if (!maybeUrl.startsWith('https://') && !maybeUrl.startsWith('http://')) {
      maybeUrl = 'https://' + maybeUrl
    }

    const url = new URL(maybeUrl)

    // Extract the hostname and split it into labels
    const hostname = url.hostname
    const labels = hostname.split('.')

    // Ensure there are at least two labels (e.g., 'example' and 'com')
    if (labels.length < 2) return null

    const tld = labels[labels.length - 1]

    // Check that the TLD is at least two characters long and contains only letters
    if (!/^[a-z]{2,}$/i.test(tld)) return null

    return url.toString()
  } catch {
    return null
  }
}
